import React, { useMemo } from "react";
import { motion, type Variants } from "framer-motion";
import { Gem, Compass, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

const WhyUs: React.FC = () => {
  const { t } = useTranslation();

  const items = useMemo(
    () => [
      {
        id: "curated",
        icon: Gem,
        title: t("whyUs.items.curated.title"),
        text: t("whyUs.items.curated.text"),
      },
      {
        id: "tailored",
        icon: Compass,
        title: t("whyUs.items.tailored.title"),
        text: t("whyUs.items.tailored.text"),
      },
      {
        id: "local",
        icon: Shield,
        title: t("whyUs.items.local.title"),
        text: t("whyUs.items.local.text"),
      },
    ],
    [t]
  );

  /* ================= ANIMATIONS ================= */
  const containerVariants: Variants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.18,
      },
    },
  };

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 48,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="relative py-40 md:py-52 px-4 sm:px-6 overflow-hidden bg-cream">
      <div className="max-w-7xl mx-auto">
        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-24"
        >
          <span className="block font-oswald font-light uppercase tracking-[0.45em] text-stone-400 text-[10px] mb-6">
            {t("whyUs.eyebrow")}
          </span>

          <h2 className="font-oswald font-light uppercase tracking-wide text-4xl sm:text-5xl md:text-6xl text-stone-800 leading-[1.1]">
            {t("whyUs.title.line1")}
            <br className="hidden sm:block" />
            {t("whyUs.title.line2")}
          </h2>

          <p className="mt-10 font-playfair italic text-base sm:text-lg text-stone-500/70">
            {t("whyUs.subtitle")}
          </p>
        </motion.div>

        {/* ================= GRID ================= */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-14"
        >
          {items.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={item.id}
                variants={cardVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="
                  group relative
                  px-10 py-14
                  rounded-[30px]
                  bg-white
                  border border-stone-100
                  transition-all duration-700
                  hover:shadow-[0_40px_90px_-15px_rgba(0,0,0,0.08)]
                  hover:border-stone-200
                "
              >
                {/* INDEX */}
                <span
                  className="
                    absolute top-8 right-8
                    font-oswald
                    font-light
                    text-[4rem]
                    leading-none
                    text-stone-100
                    pointer-events-none
                    group-hover:text-stone-200
                    transition-colors duration-500
                  "
                >
                  0{index + 1}
                </span>

                {/* GOLD LINE */}
                <span className="block w-16 h-px mb-12 bg-[#a5694f]/50 transition-all duration-500 group-hover:w-24 group-hover:bg-[#a5694f]" />

                {/* ICON */}
                <div
                  className="
                    w-16 h-16 mb-10
                    flex items-center justify-center
                    rounded-full
                    border border-stone-200
                    bg-stone-50
                    transition-all duration-500
                    group-hover:scale-110
                    group-hover:border-[#a5694f]/30
                    group-hover:bg-[#a5694f]/5
                  "
                >
                  <Icon className="w-6 h-6 text-stone-400 group-hover:text-[#a5694f] transition-colors duration-500" />
                </div>

                {/* CONTENT */}
                <h3 className="font-oswald font-light tracking-widest uppercase text-xl text-stone-800 mb-6 group-hover:text-[#a5694f] transition-colors duration-500">
                  {item.title}
                </h3>

                <p className="text-stone-500 leading-relaxed text-sm sm:text-base font-sans font-light">
                  {item.text}
                </p>

                {/* HOVER LIGHT */}
                <div
                  className="
                    absolute inset-0
                    rounded-[2.5rem]
                    bg-gradient-to-br
                    from-gold/10
                    to-transparent
                    opacity-0
                    group-hover:opacity-100
                    transition-opacity duration-700
                    pointer-events-none
                  "
                />
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUs;
